import { Routes } from '@angular/router';

import { AlertStatusComponent } from './alerts/components/alert-status/alert-status.component';
import { AuthenticationGuardDataType, AuthenticationGuardService } from './shared/authentication/authentication-guard.service';
import { AzureAdSettingsComponent } from './azure-ad/azure-ad-settings/azure-ad-settings.component';
import { CancelMessageStatusComponent } from './tracking/tracking-cancelled-messages/tracking-cancelled-messages.component';
import { ChangeLogComponent } from './change-log/change-log.component';
import { ChartComponent } from './chart';
import { ConcurrencyKeysComponent } from './configuration/concurrency-keys/concurrency-keys.component';
import { ConfigurationSettingsComponent } from './configuration/configuration-settings/configuration-settings.component';
import { ControlTestComponent } from './test/control-test/control-test.component';
import { DataGroupCrudCanDeactivate } from './user/data-group-crud-can-deactivate';
import { DataGroupCrudComponent } from './user/data-group-crud.component';
import { DeveloperAssemblyArtifactsComponent } from './developer/developer-artifacts/developer-assembly-artifacts.component';
import { DeveloperBatchingConfigurationsComponent } from './developer/developer-batching-configurations/developer-batching-configurations.component';
import { DeveloperConfigurationKeysComponent } from './developer/developer-configuration-keys/developer-configuration-keys.component';
import { DeveloperDistributionObjectGroupsComponent } from './developer/developer-distribution-object-groups/developer-distribution-object-groups.component';
import { DeveloperDocumentConfigsComponent } from './developer/developer-document-configs/developer-document-configs.component';
import { DeveloperDocumentTypeClassifierResolversComponent } from './developer/developer-document-type-classifier-resolver/developer-document-type-classifier-resolver.component';
import { DeveloperDocumentTypeClassifiersComponent } from './developer/developer-document-type-classifier/developer-document-type-classifier.component';
import { DeveloperDocumentTypesComponent } from './developer/developer-document-types/developer-document-types.component';
import { DeveloperDuplicateCheckTypesComponent } from './developer/developer-duplicate-check-types/developer-duplicate-check-types.component';
import { DeveloperErrorCodesComponent } from './developer/developer-error-codes/developer-error-codes.component';
import { DeveloperExportComponent } from './developer/export/developer-export/developer-export.component';
import { DeveloperFileNamingTypesComponent } from './developer/developer-file-naming-types/developer-file-naming-types.component';
import { DeveloperFormatsComponent } from './developer/developer-formats/developer-formats.component';
import { DeveloperFormatTypesComponent } from './developer/developer-format-types/developer-format-types.component';
import { DeveloperImportComponent } from './developer/import/developer-import/developer-import.component';
import { DeveloperInitStepsComponent } from './developer/developer-init-steps/developer-init-steps.component';
import { DeveloperIntegratedFtpProvidersComponent } from './developer/developer-integrated-ftp-providers/developer-integrated-ftp-providers.component';
import { DeveloperItineraryStepsComponent } from './developer/developer-itinerary-steps/developer-itinerary-steps.component';
import { DeveloperNugetFeedsComponent } from './developer/developer-nuget-feeds/developer-nuget-feeds.component';
import { DeveloperPageComponent } from './developer/developer-page/developer-page.component';
import { DeveloperPartnerIdentificationTypesComponent } from './developer/developer-partner-identification-types/developer-partner-identification-types.component';
import { DeveloperStatusEventsComponent } from './developer/developer-status-events/developer-status-events.component';
import { DeveloperSystemInitStepsComponent } from './developer/developer-system-init-steps/developer-system-init-steps.component';
import { DeveloperSystemIntegratedFtpProviderComponent } from './developer/developer-system-integrated-ftp-provider/developer-system-integrated-ftp-provider.component';
import { DeveloperTrackingFieldsComponent } from './developer/developer-tracking-fields/developer-tracking-fields.component';
import { DeveloperTrackingUiActionsComponent } from './developer/developer-tracking-ui-actions/developer-tracking-ui-actions.component';
import { DeveloperTransportTypesComponent } from './developer/developer-transport-types/developer-transport-types.component';
import { DeveloperVariantsComponent } from './developer/developer-variants/developer-variants.component';
import { DeveloperVariantVersionsComponent } from './developer/developer-variant-versions/developer-variant-versions.component';
import { DeveloperXmlArtifactsComponent } from './developer/developer-artifacts/developer-xml-artifacts.component';
import { DeveloperXmlPropertyPromotionsComponent } from './developer/developer-xml-property-promotions/developer-xml-property-promotions.component';
import { DistributionImportComponent } from './developer/import/distribution-import/distribution-import.component';
import { DistributionSearchComponent } from './distribution/distribution-search.component';
import { DistributionSearchResultComponent } from './distribution/distribution-search-result.component';
import { DistributionSearchResultDetailsComponent, DistributionSearchResultDetailsCanDeactivate } from './distribution/distribution-search-result-details.component';
import { DocumentRetentionComponent } from './maintenance/document-retention/document-retention.component';
import { ErrorRulesDetailsComponent } from './error-rules/error-rules-details/error-rules-details.component';
import { ErrorRulesOverviewComponent } from './error-rules/error-rules-overview/error-rules-overview.component';
import { GenericErrorHandlingComponent } from './shared/generic-error-handling/generic-error-handling.component';
import { HttpHandlersComponent } from './transport/http-handlers/http-handlers.component';
import { IdentityClientsComponent } from './identity/identity-clients/identity-clients.component';
import { IdentityTokensComponent } from './identity/identity-tokens/identity-tokens.component';
import { ImportExportSettingsComponent } from './configuration/importexport-settings/importexport-settings.component';
import { LoggedInGuardService } from './shared/authentication/logged-in-guard.service';
import { LoginComponent } from './login/login.component';
import { MailLogComponent } from './configuration/mail-log/mail-log.component';
import { MailSettingsComponent } from './configuration/mail-settings/mail-settings.component';
import { MonitorGroupsComponent } from './monitor/components/monitor-groups/monitor-groups.component';
import { MonitorItemCanDeactivate } from './monitor/components/monitor-item/monitor-item-can-deactivate';
import { MonitorItemComponent } from './monitor/components/monitor-item/monitor-item.component';
import { MonitorItemsComponent } from './monitor/components/monitor-items/monitor-items.component';
import { MonitorSchedulesComponent } from './monitor/components/monitor-schedules/monitor-schedules.component';
import { MonitorStatusComponent } from './monitor/components/monitor-status/monitor-status.component';
import { MyUserProfileComponent } from './user/user-my-profile.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { PartnerContactTypesComponent } from './partner/partner-contact-types.component';
import { PartnerImportComponent } from './developer/import/partner-import/partner-import.component';
import { PartnerSearchComponent } from './partner/partner-search.component';
import { PartnerSearchDetailsComponent, PartnerSearchDetailsCanDeactivate } from './partner/partner-search-details.component';
import { PartnerSystemsComponent } from './partner/partner-systems/partner-systems.component';
import { PartnerTagsComponent } from './partner/partner-tags.component';
import { ReportsEditReportComponent } from './reports/components/reports-edit-report/reports-edit-report.component';
import { ReportsOverviewComponent } from './reports/components/reports-overview/reports-overview.component';
import { ReportsRunReportComponent } from './reports/components/reports-run-report/reports-run-report.component';
import { RestrictedAccessComponent } from './restricted-access/restricted-access.component';
import { RetryOverviewComponent } from './tracking/retry-overview/retry-overview.component';
import { SchedulerLogComponent } from './configuration/scheduler/scheduler-log/scheduler-log.component';
import { SchedulerTaskListComponent } from './configuration/scheduler/scheduler-task-list/scheduler-task-list.component';
import { SchedulerTaskListDetailsComponent } from './configuration/scheduler/scheduler-task-list-details/scheduler-task-list-details.component';
import { SequencesComponent } from './configuration/sequences/sequences.component';
import { StakeholderComponent } from './stakeholder/stakeholder.component';
import { TestToolCompareRulesComponent } from './test-tool/test-tool-compare-rules/test-tool-compare-rules.component';
import { TestToolTestCaseInterchangesComponent } from './test-tool/test-tool-test-case-interchanges/test-tool-test-case-interchanges.component';
import { TestToolTestCasesComponent } from './test-tool/test-tool-test-cases/test-tool-test-cases.component';
import { TestToolTestCasesCreateComponent } from './test-tool/test-tool-test-cases-create/test-tool-test-cases-create.component';
import { TestToolTestRunResultsComponent } from './test-tool/test-tool-test-run-results/test-tool-test-run-results.component';
import { TestToolTestRunsComponent } from './test-tool/test-tool-test-runs/test-tool-test-runs.component';
import { TestToolVisualCompareComponent } from './test-tool/test-tool-visual-compare/test-tool-visual-compare.component';
import { TrackingDocumentCompareComponent } from './tracking/tracking-document-compare/tracking-document-compare.component';
import { TrackingSearchComponent } from './tracking/tracking-search.component';
import { TrackingSearchResultComponent } from './tracking/tracking-search-result.component';
import { TrackingSearchResultDetailsComponent } from './tracking/tracking-search-result-details.component';
import { TrackingSearchStatusEventComponent } from './tracking/tracking-search-status-events/tracking-search-status-events.component';
import { TrackingSearchStatusEventResultComponent } from './tracking/tracking-search-status-events-result/tracking-search-status-events-result.component';
import { TrackingSearchViewComponent } from './tracking/tracking-search-views/tracking-search-views.component';
import { TransportImportComponent } from './transport/import/transport-import/transport-import.component';
import { TransportLocationsDetailsCanDeactivate } from './transport/incoming/transport-locations-details/transport-locations-details-can-deactivate';
import { TransportLocationsDetailsComponent } from './transport/incoming/transport-locations-details/transport-locations-details.component';
import { TransportLocationsListComponent } from './transport/incoming/transport-locations-list/transport-locations-list.component';
import { TransportLocationsLogComponent } from './transport/incoming/transport-locations-log/transport-locations-log.component';
import { TransportOutLocationsDetailsComponent } from './transport/outgoing/transport-out-locations-details/transport-out-locations-details.component';
import { TransportOutLocationsListComponent } from './transport/outgoing/transport-out-locations-list/transport-out-locations-list.component';
import { UnsupportedBrowserComponent } from './unsupported-browser/unsupported-browser.component';
import { UserGroupCrudComponent } from './user/user-group-crud.component';
import { UserRoleCrudComponent } from './user/user-role-crud.component';
import { UserRoleDetailsComponent } from './user/user-role-details/user-role-details.component';
import { UserSearchComponent } from './user/user-search.component';
import { UserSearchDetailsComponent, UserSearchDetailsComponentCanDeactivate } from './user/user-search-details.component';
import { MailTemplatesListComponent } from './mail-templates/mail-templates-list/mail-templates-list.component';
import { MailTemplatesDetailsComponent } from './mail-templates/mail-templates-details/mail-templates-details.component';
import { FormPageTestComponent } from './test/form-page-test/form-page-test.component';
import { DeveloperSchedulerTaskHandlersComponent } from './developer/developer-scheduler-task-handlers/developer-scheduler-task-handlers.component';
import { PartnerportalComponent } from './partnerportal/partnerportal.component';
import { DeveloperContextPropertiesComponent } from './developer/developer-context-properties/developer-context-properties.component';
import { TestToolCompareRulesImportComponent } from './test-tool/test-tool-compare-rules-import/test-tool-compare-rules-import.component';
import { AdvancedHttpHandlersComponent } from './transport/advanced-http-handlers/advanced-http-handlers.component';
import { McpServersComponent } from './mcp/mcp-servers/mcp-servers.component';
import { CompanyProfileComponent } from './configuration/company-profile/company-profile.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { ControlTestReactiveFormsComponent } from './test/control-test-reactive-forms/control-test-reactive-forms.component';
import { ControlTestTemplateFormsComponent } from './test/control-test-template-forms/control-test-template-forms.component';
import { ControlTestTwoWayBindingComponent } from './test/control-test-two-way-binding/control-test-two-way-binding.component';
import { ControlTestDecoratorsComponent } from './test/control-test-decorators/control-test-decorators.component';

export const routes: Routes = [
    {
        path: '',
        data: { breadcrumb: 'Home' },
        //children: [
        //    {
        //        path: '',
        //        component: FrontpageComponent,
        //        canActivate: [AuthenticationGuardService, LoggedInGuardService],
        //        data: { authenticationData: {} },
        //    }
        //]
        redirectTo: 'tracking', pathMatch: 'full'
    },
    {
        path: 'login',
        component: LoginComponent
    },
    {
        path: 'reset-password',
        component: ResetPasswordComponent
    },
    {
        path: 'partnerportal',
        component: PartnerportalComponent
    },
    {
        path: 'unsupported-browser',
        component: UnsupportedBrowserComponent
    },
    {
        path: 'user',
        data: {
            breadcrumb: 'User'
        },
        children: [
            {
                path: '',
                component: UserSearchComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Users_View_Search']
                    }
                },
            },
            {
                path: 'details',
                data: {
                    breadcrumb: 'Details',
                },
                children: [
                    {
                        path: '',
                        component: UserSearchDetailsComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        canDeactivate: [UserSearchDetailsComponentCanDeactivate],
                        data: {
                            authenticationData: {
                                requiredPermissions: ['Users_View_UserDetails']
                            }
                        },
                    }
                ]
            },
            {
                path: 'usergroups',
                component: UserGroupCrudComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Usergroups',
                    authenticationData: {
                        requiredPermissions: ['Users_View_UserGroups']
                    }
                }
            },
            {
                path: 'datagroups',
                component: DataGroupCrudComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                canDeactivate: [DataGroupCrudCanDeactivate],
                data: {
                    breadcrumb: 'Datagroups',
                    authenticationData: {
                        requiredPermissions: ['Users_View_DataGroups']
                    }
                }
            },
            {
                path: 'roles',
                data: {
                    breadcrumb: 'Roles',
                },
                children: [
                    {
                        path: '',
                        component: UserRoleCrudComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            authenticationData: {
                                requiredPermissions: ['Users_View_UserRoles']
                            }
                        }
                    },
                    {
                        path: 'details',
                        component: UserRoleDetailsComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            breadcrumb: 'Details',
                            authenticationData: {
                                requiredPermissions: ['Users_View_UserRoles']
                            }
                        },
                    }
                ]
            }
        ]
    },
    {
        path: 'charts',
        data: {
            breadcrumb: 'Charts'
        },
        children: [
            {
                path: '',
                component: ChartComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Charts_View_Charts']
                    }
                },
            }
        ]
    },
    {
        path: 'partner',
        data: {
            authenticationData: {
                requiredPermissions: ['Partner_View_Search']
            },
            breadcrumb: 'Partner',
        },
        children: [
            {
                path: '',
                component: PartnerSearchComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        authenticationType: AuthenticationGuardDataType.And,
                        requiredPermissions: ['Partner_View_Search', 'Partner_Action_GeneralShow']
                    }
                }
            },
            {
                path: 'details',
                component: PartnerSearchDetailsComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                canDeactivate: [PartnerSearchDetailsCanDeactivate],
                data: {
                    breadcrumb: 'Details',
                    authenticationData: {
                        requiredPermissions: ['Partner_View_PartnerDetails']
                    }
                },
            },
            {
                path: 'tags',
                component: PartnerTagsComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        authenticationType: AuthenticationGuardDataType.And,
                        requiredPermissions: ['Partner_View_Tags', 'Partner_Action_GeneralShow'],
                    },
                    breadcrumb: 'Tags'
                },
            },
            {
                path: 'system',
                component: PartnerSystemsComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Partner_View_Systems'],
                    },
                    breadcrumb: 'Systems'
                },
            },
            {
                path: 'contacttypes',
                component: PartnerContactTypesComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Partner_View_Search'],
                    },
                    breadcrumb: 'Contact Types'
                },
            },
            {
                path: 'import',
                component: PartnerImportComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Partner_Action_PartnerImport'],
                    },
                    breadcrumb: 'Import'
                },
            }
        ]
    },
    {
        path: 'myprofile',
        component: MyUserProfileComponent,
        data: {
            breadcrumb: 'My Profile',
            authenticationData: {}
        },
        canActivate: [AuthenticationGuardService, LoggedInGuardService],
    },
    {
        path: 'companyprofile',
        component: CompanyProfileComponent,
        data: {
            breadcrumb: 'Company Profile',
            authenticationData: {}
        },
        canActivate: [AuthenticationGuardService, LoggedInGuardService],
    },
    {
        path: 'distribution',
        data: {
            breadcrumb: 'Distribution',
        },
        children: [
            {
                path: '',
                component: DistributionSearchComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Distribution_View_Search']
                    }
                }
            },
            {
                path: 'result',
                data: {
                    breadcrumb: 'Result',
                },
                children: [
                    {
                        path: '',
                        component: DistributionSearchResultComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            authenticationData: {
                                requiredPermissions: ['Distribution_View_SearchResult']
                            }
                        }
                    },
                    {
                        path: 'details',
                        data: {
                            breadcrumb: 'Details',
                        },
                        children: [
                            {
                                path: '',
                                component: DistributionSearchResultDetailsComponent,
                                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                                canDeactivate: [DistributionSearchResultDetailsCanDeactivate],
                                data: {
                                    authenticationData: {
                                        requiredPermissions: ['Distribution_View_DistributionDetails']
                                    }
                                }
                            }
                        ]
                    }
                ]
            },
            {
                path: 'import',
                component: DistributionImportComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Distribution_Action_DistributionImport'],
                    },
                    breadcrumb: 'Import'
                },
            }
            // {
            //     path: 'agreements',
            //     component: DistributionAgreementComponent,
            //     // resolve: { agreements: DistributionAgreementService },
            //     canActivate: [AuthenticationGuardService, LoggedInGuardService],
            //     data: {
            //         breadcrumb: 'Agreements',
            //     }
            // }
        ]
    },
    {
        path: 'test',
        data: {
            breadcrumb: 'Test'
        },
        children: [{
            path: '',
            component: ControlTestComponent,
            canActivate: [AuthenticationGuardService, LoggedInGuardService],
            children: [{
                path: '',
                redirectTo: 'decorator',
                pathMatch: 'full',
            }, {
                path: 'reactive',
                component: ControlTestReactiveFormsComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Reactive Forms'
                },
            }, {
                path: 'template',
                component: ControlTestTemplateFormsComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Template Forms'
                },
            }, {
                path: 'two-way',
                component: ControlTestTwoWayBindingComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Two Way Binding'
                },
            }, {
                path: 'decorator',
                component: ControlTestDecoratorsComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Controls and Decorators'
                },
            }, {
                path: 'form-page',
                component: FormPageTestComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Form Page'
                },
            }]
        }]
    },
    {
        path: 'monitor',
        data: {
            breadcrumb: 'Monitor',
        },
        children: [
            {
                path: '',
                component: MonitorStatusComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Status',
                    authenticationData: { requiredPermissions: ['Monitor_View_Status'] }
                }
            },
            {
                path: 'groups',
                component: MonitorGroupsComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Groups',
                    authenticationData: { requiredPermissions: ['Monitor_View_Groups'] }
                }
            },
            {
                path: 'items',
                data: {
                    breadcrumb: 'Items',
                    authenticationData: { requiredPermissions: ['Monitor_View_Items'] }
                },
                children: [
                    {
                        path: '',
                        component: MonitorItemsComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                    },
                    {
                        path: 'item',
                        component: MonitorItemComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        canDeactivate: [MonitorItemCanDeactivate],
                        data: {
                            breadcrumb: 'Item',
                            authenticationData: { requiredPermissions: ['Monitor_View_Items'] }
                        }
                    },
                ]
            },
            {
                path: 'schedules',
                component: MonitorSchedulesComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Schedules',
                    authenticationData: { requiredPermissions: ['Monitor_View_Schedules'] }
                }
            }
        ]
    },
    {
        path: 'tracking',
        data: {
            breadcrumb: 'Tracking',
        },
        children: [
            {
                path: '',
                component: TrackingSearchComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Tracking_View_Search']
                    }
                }
            },
            {
                path: 'searchstatusevents',
                data: {
                    breadcrumb: 'Search Status Events'
                },
                children: [
                    {
                        path: '',
                        component: TrackingSearchStatusEventComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            authenticationData: {
                                requiredPermissions: ['TrackingStatusEvent_View_Search']
                            }
                        }
                    },
                    {
                        path: 'result',
                        data: {
                            breadcrumb: 'Result'
                        },
                        children: [
                            {
                                path: '',
                                component: TrackingSearchStatusEventResultComponent,
                                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                                data: {
                                    authenticationData: {
                                        requiredPermissions: ['Tracking_View_SearchResult']
                                    }
                                }
                            }
                        ]
                    }
                ]
            },
            {
                path: 'result',
                data: { breadcrumb: 'Result' },
                children: [
                    {
                        path: '',
                        component: TrackingSearchResultComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            authenticationData: {
                                requiredPermissions: ['Tracking_View_SearchResult']
                            }
                        }
                    },
                    {
                        path: 'details',
                        component: TrackingSearchResultDetailsComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            breadcrumb: 'Details',
                            authenticationData: {
                                requiredPermissions: ['Tracking_View_DocumentDetails']
                            }
                        }
                    },
                    {
                        path: 'compare',
                        component: TrackingDocumentCompareComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            breadcrumb: 'Compare',
                            authenticationData: {
                                requiredPermissions: ['Tracking_Action_CompareFiles']
                            }
                        }
                    }
                ]
            },
            {
                path: 'views',
                component: TrackingSearchViewComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: { breadcrumb: 'Views' },
            },
            {
                path: 'cancelmessagestatus',
                component: CancelMessageStatusComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Cancel Message Status',
                    authenticationData: {
                        requiredPermissions: ['Tracking_View_CancelMessageStatus']
                    }
                }
            },
            {
                path: 'active-retries',
                component: RetryOverviewComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: { breadcrumb: 'Active Retries' }
            },
        ]
    },
    {
        path: 'settings',
        data: {
            breadcrumb: 'Settings'
        },
        children: [
            {
                path: 'settings',
                component: ConfigurationSettingsComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Settings_View_Settings'],
                    },
                    breadcrumb: 'Settings'
                },
            },
            {
                path: 'mail',
                component: MailSettingsComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: { breadcrumb: 'Mail' },
            },
            {
                path: 'mail-templates',
                data: { breadcrumb: 'Mail Templates' },
                children: [
                    {
                        path: '',
                        component: MailTemplatesListComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            authenticationData: {
                                requiredPermissions: ['Mail_View_Templates']
                            }
                        }
                    },
                    {
                        path: 'details',
                        component: MailTemplatesDetailsComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            breadcrumb: 'Mail Template'
                        }
                    }
                ]
            },
            {
                path: 'mail-log',
                component: MailLogComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: { breadcrumb: 'Mail Log' },
            },
            {
                path: 'changelog',
                component: ChangeLogComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Show change log'
                },
            },
            {
                path: 'document-retention',
                component: DocumentRetentionComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Document retention'
                },
            },
            {
                path: 'scheduler',
                data: {
                    breadcrumb: 'Scheduling'
                },
                children: [
                    {
                        path: '',
                        component: SchedulerTaskListComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            authenticationData: {
                                requiredPermissions: ['Scheduler_View_Scheduling']
                            }
                        }
                    },
                    {
                        path: 'details',
                        component: SchedulerTaskListDetailsComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            breadcrumb: 'Details'
                        },
                    },
                    {
                        path: 'log',
                        component: SchedulerLogComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            breadcrumb: 'Log',
                            authenticationData: { requiredPermissions: ['Scheduler_Action_Scheduling_ShowLog'] }
                        },
                    }
                ]
            },
            {
                path: 'sequences',
                component: SequencesComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Sequences'
                },
            },
            {
                path: 'concurrencykeys',
                component: ConcurrencyKeysComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Concurrency Keys',
                }
            },
            {
                path: 'importexport',
                component: ImportExportSettingsComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: { breadcrumb: 'ImportExport' },
            },
        ]
    },
    {
        path: 'developer',
        data: {
            breadcrumb: 'Developer'
        },
        children: [
            {
                path: '',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    }
                },
                component: DeveloperPageComponent
            },
            {
                path: 'formattypes',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Format types',
                },
                component: DeveloperFormatTypesComponent
            },
            {
                path: 'formats',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Formats',
                },
                component: DeveloperFormatsComponent
            },
            {
                path: 'variants',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Variants',
                },
                component: DeveloperVariantsComponent
            },
            {
                path: 'versions',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Versions',
                },
                component: DeveloperVariantVersionsComponent
            },
            {
                path: 'doctype',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Document types',
                },
                component: DeveloperDocumentTypesComponent
            },
            {
                path: 'doctypegroups',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Document type groups',
                },
                component: DeveloperDistributionObjectGroupsComponent
            },
            {
                path: 'docconfigs',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Document configurations',
                },
                component: DeveloperDocumentConfigsComponent
            },
            {
                path: 'filenamingtypes',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'File naming types',
                },
                component: DeveloperFileNamingTypesComponent
            },
            {
                path: 'itineraries',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Itinerary steps',
                },
                component: DeveloperItineraryStepsComponent
            },
            {
                path: 'errorcodes',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Error codes',
                },
                component: DeveloperErrorCodesComponent
            },
            {
                path: 'xmlpropertypromotions',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'XML Property Promotions',
                },
                component: DeveloperXmlPropertyPromotionsComponent
            },
            {
                path: 'configkey',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Configuration keys',
                },
                component: DeveloperConfigurationKeysComponent
            },
            {
                path: 'tracking',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Tracking fields',
                },
                component: DeveloperTrackingFieldsComponent
            },
            {
                path: 'export',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_Action_Export'],
                    },
                    breadcrumb: 'Export',
                },
                component: DeveloperExportComponent
            },
            {
                path: 'import',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_Action_Import'],
                    },
                    breadcrumb: 'Import',
                },
                component: DeveloperImportComponent
            },
            {
                path: 'assemblyartifacts',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Assembly Artifacts',
                },
                component: DeveloperAssemblyArtifactsComponent
            },
            {
                path: 'xmlartifacts',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Xml Artifacts',
                },
                component: DeveloperXmlArtifactsComponent
            },
            {
                path: 'nugetfeeds',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Nuget feeds',
                },
                component: DeveloperNugetFeedsComponent
            },
            {
                path: 'contextproperties',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Context Properties',
                },
                component: DeveloperContextPropertiesComponent
            },
            {
                path: 'statusevents',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Status Events',
                },
                component: DeveloperStatusEventsComponent
            },
            {
                path: 'transport-types',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Transport Types',
                },
                component: DeveloperTransportTypesComponent
            },
            {
                path: 'init-steps',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Init Steps',
                },
                component: DeveloperInitStepsComponent
            },
            {
                path: 'system-init-steps',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'System Init Steps',
                },
                component: DeveloperSystemInitStepsComponent
            },
            {
                path: 'system-integrated-ftp-provider',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'System Integrated FTP Provider'
                },
                component: DeveloperSystemIntegratedFtpProviderComponent
            },
            {
                path: 'integrated-ftp-providers',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Integrated FTP Providers'
                },
                component: DeveloperIntegratedFtpProvidersComponent
            },
            {
                path: 'duplicate-checks',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Duplicate Checks',
                },
                component: DeveloperDuplicateCheckTypesComponent
            },
            {
                path: 'document-type-classifier',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Document Type Classifiers',
                },
                component: DeveloperDocumentTypeClassifiersComponent
            },
            {
                path: 'document-type-classifier-resolver',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Document Type Classifier Resolvers',
                },
                component: DeveloperDocumentTypeClassifierResolversComponent
            },
            {
                path: 'batchingconfigurations',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Batching Configurations',
                },
                component: DeveloperBatchingConfigurationsComponent
            },
            {
                path: 'identification',
                component: DeveloperPartnerIdentificationTypesComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Identification types'
                },
            },
            {
                path: 'tracking-ui-actions',
                component: DeveloperTrackingUiActionsComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Tracking UI actions'
                },
            },
            {
                path: 'scheduler-task-handlers',
                component: DeveloperSchedulerTaskHandlersComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Scheduler Task Handlers'
                },
            },
        ]
    },
    {
        path: 'test-tool',
        data: {
            authenticationData: {
                requiredPermissions: ['TestTool_View']
            },
            breadcrumb: 'Test tool',
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'test-cases'
            },
            {
                path: 'test-cases',
                data: {
                    breadcrumb: 'Test cases',
                    authenticationData: {
                        requiredPermissions: ['TestTool_View']
                    },
                },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            authenticationData: {
                                requiredPermissions: ['TestTool_View']
                            }
                        },
                        component: TestToolTestCasesComponent
                    },
                    {
                        path: 'test-case-interchanges',
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            breadcrumb: 'Test case interchanges',
                            authenticationData: {
                                requiredPermissions: ['TestTool_View']
                            }
                        },
                        component: TestToolTestCaseInterchangesComponent
                    }
                ]
            },
            {
                path: 'create-test-case',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['TestTool_Action_TestCase_Create']
                    },
                    breadcrumb: 'Create test case',
                },
                component: TestToolTestCasesCreateComponent
            },
            {
                path: 'test-runs',
                data: {
                    breadcrumb: 'Test runs',
                },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        component: TestToolTestRunsComponent
                    },
                    {
                        path: 'test-run-results',
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            breadcrumb: 'Test run results',
                        },
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                component: TestToolTestRunResultsComponent
                            },
                            {
                                path: 'compare',
                                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                                data: {
                                    breadcrumb: 'Compare',
                                },
                                component: TestToolVisualCompareComponent,
                            }
                        ]
                    }
                ]
            },
            {
                path: 'compare-rules',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Compare Rules',
                },
                component: TestToolCompareRulesComponent
            },
            {
                path: 'compare-rules-import',
                component: TestToolCompareRulesImportComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['TestTool_Action_Compare_Rules_Import'],
                    },
                    breadcrumb: 'Import compare rules'
                },
            }
        ]
    },
    {
        path: 'alert',
        data: {
            breadcrumb: 'Alerts'
        },
        children: [
            {
                path: 'status',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Status',
                    authenticationData: {
                        requiredPermissions: ['Alert_View_Status']
                    }
                },
                component: AlertStatusComponent
            }
        ]
    },
    {
        path: 'mcp',
        data: { breadcrumb: 'Mcp' },
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'mcp-servers'
            },
            {
                path: 'mcp-servers',
                component: McpServersComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['McpServer_View'],
                    },
                    breadcrumb: 'MCP servers',
                }
            },
        ]
    },
    {
        path: 'transport-locations',
        data: { breadcrumb: 'Transport' },
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'incoming'
            },
            {
                path: 'incoming',
                data: { breadcrumb: 'Incoming' },
                children: [
                    {
                        path: '',
                        component: TransportLocationsListComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            authenticationData: { requiredPermissions: ['Transport_View_IncomingLocations_List'] }
                        }
                    },
                    {
                        path: 'details',
                        component: TransportLocationsDetailsComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        canDeactivate: [TransportLocationsDetailsCanDeactivate],
                        data: {
                            breadcrumb: 'Details',
                            authenticationData: { requiredPermissions: ['Transport_View_IncomingLocations_Details'] }
                        },
                    },
                    {
                        path: 'log',
                        component: TransportLocationsLogComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            breadcrumb: 'Log',
                            authenticationData: { requiredPermissions: ['Transport_View_IncomingLocations_Details'] }
                        },
                    }
                ]
            },
            {
                path: 'outgoing',
                data: { breadcrumb: 'Outgoing' },
                children: [
                    {
                        path: '',
                        component: TransportOutLocationsListComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            authenticationData: { requiredPermissions: ['Transport_View_OutgoingLocation_List'] }
                        }
                    },
                    {
                        path: 'details',
                        component: TransportOutLocationsDetailsComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        canDeactivate: [(component: TransportLocationsDetailsComponent) => component.canDeactivate()],
                        data: {
                            breadcrumb: 'Details',
                            authenticationData: { requiredPermissions: ['Transport_View_OutgoingLocation_Details'] }
                        },
                    },
                ]
            },
            {
                path: 'http-handlers',
                component: HttpHandlersComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Http handlers',
                }
            },
            {
                path: 'advanced-http-handlers',
                component: AdvancedHttpHandlersComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Developer_View'],
                    },
                    breadcrumb: 'Advanced http handlers',
                }
            },
            {
                path: 'import',
                component: TransportImportComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Partner_Action_PartnerImport'],
                    },
                    breadcrumb: 'Import'
                },
            },
        ]
    },
    {
        path: 'reports',
        data: {
            breadcrumb: 'Reports'
        },
        children: [
            {
                path: '',
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                component: ReportsOverviewComponent,
                data: {
                    authenticationData: {
                        requiredPermissions: ['Reports_View_ReportOverview'],
                    }
                },
            },
            {
                path: 'report',
                component: ReportsEditReportComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Report',
                    authenticationData: {
                        authenticationType: AuthenticationGuardDataType.Or,
                        requiredPermissions: ['Reports_Action_Report_Create', 'Reports_Action_Report_Edit'],
                    }
                },
            },
            {
                path: 'run',
                component: ReportsRunReportComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Run',
                    authenticationData: {
                        requiredPermissions: ['Reports_View_ReportOverview'],
                    }
                },
            }
        ]
    },
    {
        path: 'restricted-access',
        canActivate: [LoggedInGuardService],
        component: RestrictedAccessComponent,
        data: {
            breadcrumb: 'Restricted Access',
        },
    },
    {
        path: 'generic-errors',
        canActivate: [LoggedInGuardService],
        component: GenericErrorHandlingComponent,
        data: {
            breadcrumb: 'Errors',
        },
    },
    {
        path: 'error',
        data: {
            breadcrumb: 'Error'
        },
        children: [
            {
                path: 'generic-errors',
                canActivate: [LoggedInGuardService],
                component: GenericErrorHandlingComponent,
                data: {
                    breadcrumb: 'Errors',
                },
            },
            {
                path: 'error-rules',
                data: {
                    breadcrumb: 'Error Rules'
                },
                children: [
                    {
                        path: '',
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        component: ErrorRulesOverviewComponent,
                        data: {
                            authenticationData: {
                                requiredPermissions: ['ErrorRules_View'],
                            }
                        },
                    },
                    {
                        path: 'details',
                        component: ErrorRulesDetailsComponent,
                        canActivate: [AuthenticationGuardService, LoggedInGuardService],
                        data: {
                            breadcrumb: 'Details',
                            authenticationData: {
                                authenticationType: AuthenticationGuardDataType.Or,
                                requiredPermissions: ['ErrorRules_Action_Create', 'ErrorRules_Action_Edit']
                            }
                        }
                    }
                ]
            },
            {
                path: 'stakeholder',
                component: StakeholderComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    breadcrumb: 'Stakeholder',
                    authenticationData: {
                        requiredPermissions: ['Stakeholder_View']
                    }
                }
            }
        ]
    },
    {
        path: 'identity',
        data: {
            breadcrumb: 'Settings'
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'clients'
            },
            {
                path: 'clients',
                component: IdentityClientsComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Identity_View']
                    },
                    breadcrumb: 'Identity clients',
                }
            },
            {
                path: 'tokens',
                component: IdentityTokensComponent,
                canActivate: [AuthenticationGuardService, LoggedInGuardService],
                data: {
                    authenticationData: {
                        requiredPermissions: ['Identity_View']
                    },
                    breadcrumb: 'Identity tokens',
                }
            },
        ]
    },
    {
        path: 'azure-ad',
        component: AzureAdSettingsComponent,
        canActivate: [AuthenticationGuardService, LoggedInGuardService],
        data: {
            breadcrumb: 'Azure AD'
        },
    },
    {
        path: '**',
        component: PageNotFoundComponent,
        canActivate: [LoggedInGuardService]
    }
];
